import React, { useMemo } from 'react';
import { fraction } from 'mathjs';

/**
 * VariablePanel
 *
 * Assumptions / contract (pragmatic + renderer-safe):
 * - boolean  -> currentState[key] is a boolean (or truthy/falsey)
 * - integer  -> currentState[key] is a number (will be coerced to int)
 * - real     -> currentState[key] is a number
 * - fraction -> currentState[key] is stored internally as a decimal number (e.g., 0.75)
 *              but we display it as n/d using mathjs.fraction().
 *
 * If a value is missing, we fall back to def.defaultValue (if present) and then to a
 * type-appropriate default to avoid crashes.
 */
export const VariablePanel = ({ definitions = {}, currentState = {}, onChange }) => {
    const handleChange = (key, value) => {
        // parent uses setState(prev => ...)
        onChange((prev) => ({ ...prev, [key]: value }));
    };

    const coerceNumber = (v, fallback = 0) => {
        if (v == null) return fallback;
        if (typeof v === 'number' && Number.isFinite(v)) return v;
        if (typeof v === 'string') {
            const n = parseFloat(v);
            return Number.isFinite(n) ? n : fallback;
        }
        return fallback;
    };

    const coerceBoolean = (v, fallback = false) => {
        if (v == null) return fallback;
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string') {
            const t = v.trim().toLowerCase();
            if (t === 'true') return true;
            if (t === 'false') return false;
        }
        return Boolean(v);
    };

    // Accept "3/4" (string) or {numerator,denominator} (object) defensively, but store as decimal.
    const coerceFractionDecimal = (v, fallback = 0.5) => {
        if (v == null) return fallback;
        if (typeof v === 'number' && Number.isFinite(v)) return v;

        try {
            // mathjs.fraction can parse "3/4" and some numeric-ish inputs
            if (typeof v === 'string') {
                const f = fraction(v);
                return f && f.d ? f.n / f.d : fallback;
            }

            if (typeof v === 'object') {
                const n = v.n ?? v.numerator;
                const d = v.d ?? v.denominator;
                const nn = coerceNumber(n, NaN);
                const dd = coerceNumber(d, NaN);
                if (Number.isFinite(nn) && Number.isFinite(dd) && dd !== 0) return nn / dd;
            }
        } catch (e) {
            // ignore and fallback
        }

        return fallback;
    };

    const normalized = useMemo(() => {
        return Object.entries(definitions).map(([key, def]) => {
            const type = (def?.type || 'real').toLowerCase();

            // Prefer currentState, fallback to def.defaultValue, then a safe type default.
            const raw =
                currentState?.[key] !== undefined
                    ? currentState[key]
                    : def?.defaultValue !== undefined
                        ? def.defaultValue
                        : undefined;

            let value;
            if (type === 'boolean') {
                value = coerceBoolean(raw, false);
            } else if (type === 'integer') {
                value = Math.round(coerceNumber(raw, 0));
            } else if (type === 'fraction') {
                value = coerceFractionDecimal(raw, 0.5);
            } else {
                // real / slider / number / etc
                value = coerceNumber(raw, 0);
            }

            const min = def?.min ?? -10;
            const max = def?.max ?? 10;

            // Ensure range input gets a valid number within bounds (prevents React warnings)
            const clamped =
                type === 'boolean'
                    ? value
                    : Math.min(Math.max(value, min), max);

            const step =
                def?.step ??
                (type === 'integer' ? 1 : type === 'fraction' ? 0.05 : 0.1);

            return { key, def: def || {}, type, value: clamped, min, max, step };
        });
    }, [definitions, currentState]);

    const formatValue = (type, value) => {
        if (value == null) return '';
        if (type === 'fraction') {
            try {
                const f = fraction(value);
                return `${f.n}/${f.d}`;
            } catch (e) {
                return String(value);
            }
        }
        return String(value);
    };

    return (
        <div className="ch-form">
            {normalized.map(({ key, def, type, value, min, max, step }) => (
                <div key={key}>
                    {/* Header: Label and Value */}
                    <div className="flexSpaceBetween margin-b--s">
                        <label className="ch-input-label">{def.label || key}</label>
                        <span className="ch-text-bold">{formatValue(type, value)}</span>
                    </div>

                    {/* Control */}
                    {type === 'boolean' ? (
                        <label className="ch-checkbox-wrapper">
                            <input
                                type="checkbox"
                                checked={!!value}
                                onChange={(e) => handleChange(key, e.target.checked)}
                            />
                            <span className="ch-checkbox">
                <span className="ch-checkbox-inner"></span>
              </span>
                        </label>
                    ) : (
                        <input
                            type="range"
                            min={min}
                            max={max}
                            step={step}
                            value={value}
                            onChange={(e) => {
                                const raw = e.target.value;

                                if (type === 'integer') {
                                    handleChange(key, parseInt(raw, 10));
                                    return;
                                }

                                handleChange(key, parseFloat(raw));
                            }}
                            className="ch-width-100"
                        />
                    )}
                </div>
            ))}
        </div>
    );
};
